import { useEffect, useRef } from "react";

/**
 * useSimPhoneNumberAutofill — asks the React Native shell for the device's own
 * phone number once per screen visit and hands it back to the screen.
 *
 *   Android app → native opens Google's "Choose a phone number" sheet.
 *   iOS app     → native replies "unsupported"; the screen focuses its input so
 *                 the keyboard suggests the number (needs autocomplete="tel").
 *   Browser     → no-op (no ReactNativeWebView); input autocomplete still works.
 *
 * Protocol:
 *   Web → Native: { type: "GET_SIM_PHONE_NUMBER" }
 *   Native → Web: { __fromRN: true, type: "SIM_PHONE_NUMBER_RESULT",
 *                   data: { status: "ok"|"cancelled"|"unsupported"|"error",
 *                           phone?: "+E164", code?, message? } }
 *
 * The number comes from the device and is NOT verified — never lock the field,
 * log it, or treat it as verified.
 *
 * Usage:
 *   useSimPhoneNumberAutofill({
 *     enabled: true,
 *     isEmpty: () => !phone,
 *     onNumber: (e164) => setPhone(...),
 *     onUnsupported: () => document.querySelector('input[name="phone"]')?.focus(),
 *   });
 */

const SEND_DELAY_MS = 400; // let the screen paint before the sheet opens
const REPLY_TIMEOUT_MS = 60000; // older app versions never reply

export default function useSimPhoneNumberAutofill({
  enabled = true,
  isEmpty,
  onNumber,
  onUnsupported,
} = {}) {
  // Latest callbacks, so the effect doesn't re-run on every render.
  const callbacksRef = useRef({ isEmpty, onNumber, onUnsupported });
  callbacksRef.current = { isEmpty, onNumber, onUnsupported };

  // Set only once the request is actually sent — StrictMode's dev double-mount
  // clears the send timer before it fires, so the second mount still asks.
  const askedRef = useRef(false);

  useEffect(() => {
    if (!enabled || askedRef.current || !window.ReactNativeWebView) return;

    let finished = false;
    let sendTimer = null;
    let replyTimer = null;

    const fieldIsEmpty = () => {
      const check = callbacksRef.current.isEmpty;
      return typeof check === "function" ? !!check() : true;
    };

    const stop = () => {
      finished = true;
      clearTimeout(sendTimer);
      clearTimeout(replyTimer);
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };

    function handleMessage(event) {
      if (finished) return;
      let msg;
      try {
        msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // not JSON — not ours
      }
      if (!msg?.__fromRN || msg.type !== "SIM_PHONE_NUMBER_RESULT") return;

      stop();

      // User already started typing — never overwrite.
      if (!fieldIsEmpty()) return;

      const { status, phone } = msg.data || {};
      if (status === "ok" && typeof phone === "string" && phone) {
        callbacksRef.current.onNumber?.(phone);
      } else if (status === "unsupported") {
        callbacksRef.current.onUnsupported?.();
      }
      // "cancelled" / "error" → do nothing, field stays editable.
    }

    sendTimer = setTimeout(() => {
      // Already filled (e.g. from profile) — don't open the sheet.
      if (finished || !fieldIsEmpty()) return;

      // iOS delivers on window, Android on document — listen on both.
      window.addEventListener("message", handleMessage);
      document.addEventListener("message", handleMessage);

      try {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "GET_SIM_PHONE_NUMBER" })
        );
        askedRef.current = true;
        replyTimer = setTimeout(stop, REPLY_TIMEOUT_MS);
      } catch {
        stop();
      }
    }, SEND_DELAY_MS);

    return stop;
  }, [enabled]);
}
